import React, { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { FiRotateCcw } from "react-icons/fi";
import axios from "../../Utils/axiosInstance";
import toast from "react-hot-toast";
import { assets } from "./../../Assets/assets";
import AppLoader from "../../Components/Common/AppLoader";
import Pagination from "../../Components/Common/Pagination";
import { useLockBodyScroll } from "../../Components/Hooks/useLockBodyScroll";
import { useAuth } from "../../Components/Context/AuthContext";
import ReturnListItem from "../../Components/UserSideComponents/Returns/ReturnListItem";
import ReturnDetailCard from "../../Components/UserSideComponents/Returns/ReturnDetailCard";
import SupplierReturnedOrders from "./SupplierReturnedOrders";

// ─── Empty State ──────────────────────────────────────
const EmptyReturns = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <img src={assets.noordervector} alt="No Returns" className="w-64 md:w-80 mb-5 opacity-90" />
    <h2 className="text-xl font-semibold text-gray-600">No Return Requests Found</h2>
    <p className="text-sm text-gray-400 mt-1">You haven't raised any return / dispute requests yet.</p>
  </div>
);

// ─── Error State ──────────────────────────────────────
const ErrorState = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <p className="text-red-500 font-medium mb-3">Failed to load return requests</p>
    <button onClick={onRetry} className="px-4 py-2 bg-primaryColor text-white rounded-lg text-sm">
      Retry
    </button>
  </div>
);

// ─── Buyer Returns Content ────────────────────────────
const BuyerReturnsContent = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const highlightParam = searchParams.get("highlight");
  const orderNumberParam = searchParams.get("orderNumber");

  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);

  useLockBodyScroll(false);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      setError(false);

      const { data } = await axios.get(`/return/my-returns?page=${page}&limit=${limit}`);
      let returnsList = data.data || [];
      setPagination(data.pagination || null);

      // ── Keep selectedReturn in sync with fresh data, highlight param or orderNumber ──
      const targetQuery =
        highlightParam ||
        orderNumberParam ||
        location.state?.orderNumber ||
        location.state?.orderId ||
        location.state?.caseId;

      let matched = null;
      if (targetQuery) {
        matched = returnsList.find(
          (r) =>
            r.orderId?.orderNumber === targetQuery ||
            r.orderId?.orderNumber === orderNumberParam ||
            r.orderId?.orderNumber === location.state?.orderNumber ||
            r.orderId?._id === targetQuery ||
            r.orderId === targetQuery ||
            r._id === targetQuery ||
            r.uniqueCaseId === targetQuery ||
            r.uniqueCaseId === location.state?.caseId
        );

        // Fallback: If not found in current page, attempt to fetch directly by orderId
        if (!matched && (location.state?.orderId || (/^[0-9a-fA-F]{24}$/.test(targetQuery)))) {
          const fetchId = location.state?.orderId || targetQuery;
          try {
            const res = await axios.get(`/return/by-order/${fetchId}`);
            if (res.data?.success && res.data?.data) {
              matched = res.data.data;
              returnsList = [matched, ...returnsList];
            }
          } catch (err) {
            console.warn("Could not find return directly by orderId:", err?.message);
          }
        }
      }

      setReturns(returnsList);

      setSelectedReturn((prev) => {
        if (matched) return matched;
        if (prev) {
          const updated = returnsList.find((r) => r._id === prev._id);
          if (updated) return updated;
        }
        // Auto-select first return on desktop
        if (!prev && returnsList.length > 0 && window.innerWidth >= 768) {
          return returnsList[0];
        }
        return prev;
      });
    } catch (err) {
      setError(true);
      toast.error("Failed to fetch return requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReturns();
  }, [page, limit, highlightParam, orderNumberParam, location.key]);

  const filteredReturns = returns.filter((r) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      r.uniqueCaseId?.toLowerCase().includes(searchLower) ||
      r._id?.toLowerCase().includes(searchLower) ||
      r.orderId?.orderNumber?.toLowerCase().includes(searchLower)
    );
  });

  const handleReturnSelect = (returnDoc) => {
    setSelectedReturn(returnDoc);
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="2xl:max-w-7xl mx-auto px-4 md:px-8 py-4 sm:py-6 mb-16 md:mb-0">
        {/* Header - Only on mobile when no return selected, always on desktop */}
        <div className={`mb-4 sm:mb-5 ${selectedReturn && window.innerWidth < 768 ? "hidden" : "block"}`}>
          <h1 className="text-xl flex items-center gap-2 sm:text-2xl font-bold text-primaryColor mb-1">
            <FiRotateCcw />My Return Requests
          </h1>
          <p className="text-xs sm:text-sm text-gray-500">
            Track and manage your dispute cases and return shipments
          </p>
        </div>

        {/* ── Search bar — Only on mobile when no return selected, always on desktop ── */}
        <div className={`mb-4 sm:mb-6 ${selectedReturn && window.innerWidth < 768 ? "hidden" : "block"}`}>
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search by Case ID or Order Number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 pl-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primaryColor bg-white"
            />
            <svg
              className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* ── Content Grid ── */}
        {loading && returns.length === 0 ? (
          <AppLoader />
        ) : error ? (
          <ErrorState onRetry={fetchReturns} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            {/* ── Left Column: Returns List (hidden on mobile when return selected) ── */}
            <div
              className={`md:col-span-5 lg:col-span-4 ${
                selectedReturn ? "hidden md:block" : "block"
              }`}
            >
              {filteredReturns.length === 0 ? (
                <EmptyReturns />
              ) : (
                <div className="space-y-3">
                  {filteredReturns.map((returnDoc) => (
                    <ReturnListItem
                      key={returnDoc._id}
                      returnDoc={returnDoc}
                      isActive={selectedReturn?._id === returnDoc._id}
                      onClick={() => handleReturnSelect(returnDoc)}
                    />
                  ))}

                  {/* ── Pagination ── */}
                  {pagination && pagination.totalPages > 1 && (
                    <div className="pt-2">
                      <Pagination
                        currentPage={page}
                        totalPages={pagination.totalPages}
                        onPageChange={(newPage) => setPage(newPage)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Right Column: Return Detail Card (hidden on mobile when no return selected) ── */}
            <div
              className={`md:col-span-7 lg:col-span-8 ${
                selectedReturn ? "block" : "hidden md:block"
              }`}
            >
              {selectedReturn ? (
                <div>
                  {/* Mobile back button */}
                  <button
                    onClick={() => setSelectedReturn(null)}
                    className="md:hidden mb-4 flex items-center gap-2 text-sm font-semibold text-primaryColor hover:underline"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Back to return requests
                  </button>

                  <ReturnDetailCard
                    returnDoc={selectedReturn}
                    onStatusUpdate={fetchReturns}
                  />
                </div>
              ) : (
                <div className="hidden md:flex flex-col items-center justify-center p-12 bg-white rounded-xl border border-gray-200 text-center">
                  <p className="text-gray-400 text-sm font-medium">
                    Select a return request from the list to view full details
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Returns Switcher Component ───────────────────
const Returns = () => {
  const { user } = useAuth();

  if (user?.role === "supplier") {
    return <SupplierReturnedOrders />;
  }

  return <BuyerReturnsContent />;
};

export default Returns;